/**
 * Error Boundary Component
 *
 * Catches JavaScript errors in child components and displays a fallback UI.
 * Automatically recovers from chunk loading errors (stale deployments)
 * by reloading the page once.
 */

import React, { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './Button';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/** Check if an error is a chunk/module loading failure (stale deployment) */
function isChunkLoadError(error: Error): boolean {
  const msg = error.message || '';
  return (
    msg.includes('Loading chunk') ||
    msg.includes('Loading module') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    error.name === 'ChunkLoadError'
  );
}

/** Check if error is a benign DOM mutation from browser extensions (password managers, etc.) */
function isBrowserExtensionDOMError(error: Error): boolean {
  const msg = error.message || '';
  return (
    msg.includes('insertBefore') ||
    msg.includes('removeChild') ||
    msg.includes('appendChild')
  ) && (
    msg.includes('not a child of this node') ||
    msg.includes('is not a child of')
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    // Browser extensions (password managers) inject DOM nodes into forms,
    // causing React reconciliation to fail with insertBefore/removeChild errors.
    // These are benign — recover by re-rendering children.
    if (isBrowserExtensionDOMError(error)) {
      this.setState({ hasError: false, error: null, errorInfo: null });
      return;
    }

    // Auto-recover from chunk loading errors by reloading once
    if (isChunkLoadError(error)) {
      const reloaded = sessionStorage.getItem('vitbikes_error_reload');
      if (!reloaded) {
        sessionStorage.setItem('vitbikes_error_reload', '1');
        window.location.reload();
        return;
      }
      sessionStorage.removeItem('vitbikes_error_reload');
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-slate-100 mb-2">
              Etwas ist schiefgelaufen
            </h2>
            <p className="text-slate-400 mb-6">
              Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder kehren Sie zur Startseite zurück.
            </p>
            {this.state.error && (
              <div className="bg-slate-800 rounded-lg p-4 mb-6 text-left overflow-auto max-h-40">
                <p className="text-red-400 text-sm font-mono">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleRefresh} variant="secondary">
                <RefreshCw className="w-4 h-4" />
                Neu laden
              </Button>
              <Button onClick={this.handleGoHome}>
                <Home className="w-4 h-4" />
                Zur Startseite
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Page Error Boundary
 *
 * Wraps individual pages to catch errors without affecting the entire app.
 */
export function PageErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundary>{children}</ErrorBoundary>;
}
